/**
 * Build a cropped image File from react-easy-crop pixel crop area.
 * @param {string} imageSrc - object URL or data URL
 * @param {{ x: number, y: number, width: number, height: number }} pixelCrop
 * @param {{ fileName?: string, mimeType?: string, quality?: number }} [options]
 * @returns {Promise<File>}
 */
export async function getCroppedImageFile(imageSrc, pixelCrop, options = {}) {
  const {
    fileName = `kyc-cropped-${Date.now()}.jpg`,
    mimeType = 'image/jpeg',
    quality = 0.92,
  } = options;

  const image = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  const width = Math.max(1, Math.round(pixelCrop.width));
  const height = Math.max(1, Math.round(pixelCrop.height));
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not crop image.');
  }

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    width,
    height
  );

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Could not crop image.'))),
      mimeType,
      quality
    );
  });

  return new File([blob], fileName, { type: mimeType });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', () => reject(new Error('Failed to load image for crop.')));
    img.src = src;
  });
}

export function isCroppableImageFile(file) {
  if (!file) return false;
  const mime = String(file.type || '').toLowerCase();
  if (mime.startsWith('image/') && mime !== 'image/svg+xml') return true;
  const name = String(file.name || '').toLowerCase();
  return /\.(jpe?g|png|webp|gif|bmp)$/i.test(name);
}
